import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Empty, Select } from 'antd';
import dayjs from 'dayjs';
import { AlertTriangle, CalendarClock, CalendarX2, ChevronLeft, ChevronRight, Download, UserX } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import {
  INTERVIEW_STATUS, STAGE, branchById, interviewOverdue, needsSchedule, normText, pct, positionById, reqById, toDay, type Candidate, type Interview,
} from './hrLogic';
import { Badge, Kpi, PageHeader, Panel, exportXlsx, useHrPerms } from './hrUi';
import CandidateDrawer from './CandidateDrawer';
import { interviewWhen } from './RecruitPanels';

type Row = { c: Candidate; iv: Interview };
const WEEKDAY = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];

/** Lịch phỏng vấn theo tuần: việc cần xử lý (quá giờ, chưa hẹn), lịch từng ngày, lọc theo người phỏng vấn */
export default function Interviews() {
  const s = useHr();
  const { me } = useHrPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const today = toDay();
  const week = Number(params.get('week') || 0);
  const branchId = params.get('branch') || '';
  const who = params.get('who') || '';
  const mine = params.get('mine') === '1';
  const id = params.get('id');
  const start = dayjs().startOf('day').add((dayjs().day() + 6) % 7 * -1, 'day').add(week * 7, 'day');
  const end = start.add(7, 'day');

  const inScope = (c: Candidate) => !branchId || reqById(s, c.requisitionId)?.branchId === branchId;
  const matchWho = (iv: Interview) => (mine ? normText(iv.interviewer) === normText(me) : !who || iv.interviewer === who);
  const all: Row[] = useMemo(() => s.candidates.flatMap(c => (c.interviews || []).map(iv => ({ c, iv }))), [s.candidates]);
  const scoped = all.filter(r => inScope(r.c) && matchWho(r.iv));
  const overdue = scoped.filter(r => r.c.outcome === 'open' && interviewOverdue(r.iv));
  const unscheduled = s.candidates.filter(c => inScope(c) && needsSchedule(c));
  const weekRows = scoped.filter(r => r.iv.status !== 'cancelled' && r.iv.at >= start.valueOf() && r.iv.at < end.valueOf()).sort((a, b) => a.iv.at - b.iv.at);
  const todayRows = scoped.filter(r => r.iv.status === 'scheduled' && toDay(r.iv.at) === today);
  const recent = scoped.filter(r => r.iv.at >= dayjs().subtract(30, 'day').valueOf() && (r.iv.status === 'done' || r.iv.status === 'noshow'));
  const noshow = recent.filter(r => r.iv.status === 'noshow').length;
  const interviewers = Array.from(new Set(all.map(r => r.iv.interviewer))).sort();
  const label = (c: Candidate) => { const r = reqById(s, c.requisitionId); return `${positionById(s, r?.positionId)?.name || ''} · ${branchById(s, r?.branchId || '')?.short || ''}`; };

  const doExport = () => exportXlsx(`lich_phong_van_${start.format('DDMM')}`, 'Lịch phỏng vấn', weekRows.map(({ c, iv }) => ({
    'Ngày': dayjs(iv.at).format('DD/MM/YYYY'), 'Giờ': dayjs(iv.at).format('HH:mm'), 'Ứng viên': c.name, 'Điện thoại': c.phone, 'Vị trí': label(c),
    'Vòng': STAGE[iv.stage].label, 'Người phỏng vấn': iv.interviewer, 'Địa điểm': iv.place, 'Ghi chú': iv.note || '', 'Trạng thái': INTERVIEW_STATUS[iv.status].label,
  })));

  const RowItem = ({ c, iv }: Row) => {
    const w = interviewWhen(iv);
    return (
      <button type="button" onClick={() => go({ id: c.id })}
        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 grid grid-cols-[64px_1fr] md:grid-cols-[64px_1.2fr_1fr_auto] gap-x-3 gap-y-0.5 items-center text-sm">
        <span className={`font-semibold ${w.tone || 'text-gray-500'}`}>{dayjs(iv.at).format('HH:mm')}</span>
        <span className="min-w-0"><span className="font-medium">{c.name}</span><span className="block text-[11px] text-gray-400 truncate">{STAGE[iv.stage].label} · {label(c)}</span></span>
        <span className="min-w-0 text-xs text-gray-500 col-start-2 md:col-start-auto truncate">{iv.interviewer}<span className="block text-gray-400 truncate">{iv.place}</span></span>
        <span className="col-start-2 md:col-start-auto">
          {iv.status === 'scheduled' && w.overdue ? <Badge className="bg-rose-50 text-rose-700 border-rose-200">Chưa ghi kết quả</Badge> : <Badge className={INTERVIEW_STATUS[iv.status].badge}>{INTERVIEW_STATUS[iv.status].label}</Badge>}
        </span>
      </button>
    );
  };

  const days = Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Lịch phỏng vấn" desc="Lịch hẹn theo tuần của từng người phỏng vấn — bấm một dòng để mở hồ sơ, ghi kết quả hoặc đổi lịch"
          extra={<Button icon={<Download size={14} />} onClick={doExport} disabled={!weekRows.length}>Xuất lịch tuần</Button>} />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<CalendarClock size={18} />} tone="bg-sky-50 text-sky-600" label="Hôm nay" value={todayRows.length} hint={todayRows[0] ? `gần nhất ${dayjs(todayRows[0].iv.at).format('HH:mm')}` : undefined} />
          <Kpi icon={<AlertTriangle size={18} />} tone={overdue.length ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-500'} label="Quá giờ chưa ghi kết quả" value={overdue.length} />
          <Kpi icon={<CalendarX2 size={18} />} tone={unscheduled.length ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'} label="Ứng viên chưa được hẹn" value={unscheduled.length} />
          <Kpi icon={<UserX size={18} />} tone="bg-violet-50 text-violet-600" label="Tỷ lệ vắng mặt (30 ngày)" value={recent.length ? `${pct(noshow, recent.length)}%` : '—'} hint={`${noshow}/${recent.length} buổi`} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-[180px]" value={branchId} onChange={v => go({ branch: v || null })} options={[{ value: '', label: 'Tất cả chi nhánh' }, ...s.branches.map(b => ({ value: b.id, label: b.name }))]} />
          <Select className="w-[240px]" value={mine ? '' : who} disabled={mine} onChange={v => go({ who: v || null })} showSearch
            options={[{ value: '', label: 'Tất cả người phỏng vấn' }, ...interviewers.map(x => ({ value: x, label: x }))]} />
          <Checkbox checked={mine} onChange={e => go({ mine: e.target.checked ? '1' : null, who: null })}>Chỉ lịch của tôi</Checkbox>
        </div>

        {(overdue.length > 0 || unscheduled.length > 0) && (
          <Panel title="Cần xử lý">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {overdue.map(r => <RowItem key={r.iv.id} {...r} />)}
              {unscheduled.map(c => (
                <button key={c.id} type="button" onClick={() => go({ id: c.id })} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/40 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200">Chưa hẹn</Badge>
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-gray-400">{STAGE[c.stage].label} · {label(c)}</span>
                  <span className="ml-auto text-xs text-[#3e91ab]">Đặt lịch ›</span>
                </button>
              ))}
            </div>
          </Panel>
        )}

        <Panel title={`Tuần ${start.format('DD/MM')} – ${end.subtract(1, 'day').format('DD/MM/YYYY')} · ${weekRows.length} buổi`}
          extra={<div className="flex items-center gap-1">
            <Button size="small" icon={<ChevronLeft size={14} />} onClick={() => go({ week: String(week - 1) })} aria-label="Tuần trước" />
            <Button size="small" onClick={() => go({ week: null })} disabled={week === 0}>Tuần này</Button>
            <Button size="small" icon={<ChevronRight size={14} />} onClick={() => go({ week: String(week + 1) })} aria-label="Tuần sau" />
          </div>}>
          {weekRows.length === 0 ? <Empty className="py-8" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có lịch phỏng vấn trong tuần" /> : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {days.map(d => {
                const rows = weekRows.filter(r => toDay(r.iv.at) === d.format('YYYY-MM-DD'));
                if (!rows.length) return null;
                const isToday = d.format('YYYY-MM-DD') === today;
                return (
                  <div key={d.valueOf()}>
                    <div className={`px-4 py-1.5 text-xs font-semibold flex items-center gap-2 ${isToday ? 'bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-200' : 'bg-gray-50 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300'}`}>
                      {WEEKDAY[d.day()]} {d.format('DD/MM')}{isToday && <Badge className="bg-sky-600 text-white border-sky-600">Hôm nay</Badge>}<span className="font-normal text-gray-400">{rows.length} buổi</span>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">{rows.map(r => <RowItem key={r.iv.id} {...r} />)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
      <CandidateDrawer id={id} onClose={() => go({ id: null })} />
    </Layout>
  );
}
